import { createId } from '@paralleldrive/cuid2';

import { makeRequest } from './fetchClient';

import {
  SearchResponse,
  FeedbackResponse,
  APIFeedbackResponse,
  SearchResult,
  ActionInputs,
  ClientExecutionResult,
  SearchQueryAttributes,
  ConversationSummary,
  ConversationHistoryResponse,
} from '@/components/chat-search/types';
import { formatDistance } from '@/lib/formatDate';

// Retry configurations for different endpoints
const searchRetryConfig = {
  maxRetries: 3,
  baseDelay: 300, // Start with 300ms delay
  shouldRetry: (error: Error) => {
    if (error instanceof TypeError) {
      return true; // Network errors
    }
    const apiError = error as { status?: number };
    // Retry on server errors (5xx) and rate limits (429)

    return apiError.status ? apiError.status >= 500 || apiError.status === 429 : false;
  },
};

const answerRetryConfig = {
  maxRetries: 3,
  baseDelay: 500, // Start with 500ms delay for streaming responses
  shouldRetry: (error: Error) => {
    if (error instanceof TypeError) {
      return true; // Network errors
    }
    const apiError = error as { status?: number };
    // Only retry on server errors, not on client errors

    return apiError.status ? apiError.status >= 500 : false;
  },
};

interface APISearchResponse {
  data: Array<{
    id: string;
    title: string;
    url: string;
  }>;
  searchQuery: {
    id: string;
  };
  isAgenticResponse?: boolean;
}

export async function searchApi({
  endpoint,
  query,
  collectionId,
  headers,
  conversationId,
  attributes,
  secretAttributes,
  allowedRegions,
  source,
}: {
  endpoint: string;
  query: string;
  collectionId?: string | string[];
  headers?: Record<string, string>;
  conversationId?: string;
  attributes?: SearchQueryAttributes;
  secretAttributes?: string;
  allowedRegions?: string[];
  source?: string;
}): Promise<SearchResponse> {
  let resolvedCollectionId: string | string[] | undefined;
  if (Array.isArray(collectionId)) {
    resolvedCollectionId = collectionId.length > 0 ? collectionId : undefined;
  } else {
    resolvedCollectionId = collectionId && collectionId !== 'all' ? collectionId : undefined;
  }

  const payload = await makeRequest<
    APISearchResponse,
    {
      query: string;
      limit: number;
      collectionId?: string | string[];
      conversationId?: string;
      source?: string;
    }
  >(endpoint, {
    method: 'POST',
    headers,
    retry: searchRetryConfig,
    body: {
      query,
      limit: 5,
      ...(resolvedCollectionId && { collectionId: resolvedCollectionId }),
      ...(conversationId && { conversationId }),
      ...(attributes && { attributes }),
      ...(secretAttributes && { secretAttributes }),
      ...(allowedRegions && { allowedRegions }),
      ...(source && { source }),
    },
  });

  return {
    results: payload?.data,
    searchQueryId: payload?.searchQuery?.id,
    isAgenticResponse: payload?.isAgenticResponse,
  };
}

export async function fetchAnswer({
  endpoint,
  searchQueryId,
  headers,
  conversationId,
}: {
  endpoint: string;
  searchQueryId: string;
  headers?: Record<string, string>;
  conversationId?: string;
}): Promise<Response> {
  return makeRequest<
    Response,
    {
      searchQueryId: string;
      conversationId?: string;
    }
  >(endpoint, {
    method: 'POST',
    headers,
    retry: answerRetryConfig,
    isStream: true,
    fallback: {
      emptyResponse: "I apologize, but I couldn't generate an answer for your query. Please try rephrasing your question.",
      errorResponse: 'I encountered an issue while generating the answer. Please try again in a moment.',
    },
    body: {
      searchQueryId,
      ...(conversationId && { conversationId }),
    },
  });
}

export async function fetchAnswerStream({
  endpoint,
  headers,
  body,
}: {
  endpoint: string;
  headers?: Record<string, string>;
  body:
    | {
        type: 'generate-for-query';
        conversationId?: string;
        searchQueryId: string;
        profileId?: string;
        context?: { type: 'text'; text: string }[];
        attachments?: { type: string; url: string; filename?: string }[];
        parameters?: ActionInputs;
      }
    | {
        type: 'invoke-action';
        conversationId?: string;
        searchQueryId: string;
        searchIntentId: string;
        actionId: string;
        parameters: ActionInputs;
        profileId?: string;
      }
    | {
        type: 'client-execution-result';
        conversationId?: string;
        searchQueryId: string;
        searchIntentId: string;
        actionId: string;
        results: ClientExecutionResult;
        profileId?: string;
      };
}): Promise<Response> {
  return makeRequest<Response, typeof body>(endpoint, {
    method: 'POST',
    headers,
    retry: answerRetryConfig,
    isStream: true,
    fallback: {
      emptyResponse: "I apologize, but I couldn't generate an answer for your query. Please try rephrasing your question.",
      errorResponse: 'I encountered an issue while generating the answer. Please try again in a moment.',
    },
    body,
  });
}

// Helper function to generate a unique conversation ID
export function createConversationId(): string {
  return createId();
}

export async function uploadFileToApi({
  endpoint,
  conversationId,
  file,
  headers,
}: {
  endpoint: string;
  conversationId: string;
  file: File;
  headers?: Record<string, string>;
}): Promise<{ publicUrl: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('conversationId', conversationId);

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }

  const json = (await response.json()) as { data: { publicUrl: string } };

  return { publicUrl: json.data.publicUrl };
}

export async function sendFeedback({
  endpoint,
  searchQueryId,
  response,
  headers,
  conversationId,
}: {
  endpoint: string;
  searchQueryId: string;
  response: FeedbackResponse;
  headers?: Record<string, string>;
  conversationId?: string;
}): Promise<void> {
  await makeRequest<
    void,
    {
      searchQueryId: string;
      response: FeedbackResponse;
      conversationId?: string;
    }
  >(endpoint, {
    method: 'POST',
    headers,
    body: {
      searchQueryId,
      response,
      ...(conversationId && { conversationId }),
    },
  });
}

export async function sendFeedbackReason({
  endpoint,
  searchQueryId,
  reason,
  headers,
}: {
  endpoint: string;
  searchQueryId: string;
  reason: string;
  headers?: Record<string, string>;
  conversationId?: string;
}): Promise<void> {
  await makeRequest<
    void,
    {
      searchQueryId: string;
      reason: string;
      conversationId?: string;
    }
  >(endpoint, {
    method: 'POST',
    headers,
    body: {
      searchQueryId,
      reason,
    },
  });
}

export interface ConversationResponse {
  data: Array<{
    question: string;
    answer: string;
    searchQueryId: string;
    searchResults?: SearchResult[];
    searchIntentId?: string;
    feedback?: APIFeedbackResponse;
  }>;
}

export async function loadConversation({
  endpoint,
  conversationId,
  headers,
  signal,
}: {
  endpoint: string;
  conversationId: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<{ data: ConversationResponse['data'] }> {
  const payload = await makeRequest<
    ConversationResponse,
    {
      conversationId: string;
    }
  >(endpoint, {
    method: 'POST',
    headers,
    body: { conversationId },
    signal,
  });

  return { data: payload.data };
}

interface FollowUpQuestionsResponse {
  questions: string[];
}

export async function fetchFollowUpQuestions({
  endpoint,
  searchQueryId,
  headers,
  conversationId,
}: {
  endpoint: string;
  searchQueryId: string;
  headers?: Record<string, string>;
  conversationId?: string;
}): Promise<string[]> {
  const data = await makeRequest<
    FollowUpQuestionsResponse,
    {
      searchQueryId: string;
      conversationId?: string;
    }
  >(endpoint, {
    method: 'POST',
    headers,
    body: {
      searchQueryId,
      ...(conversationId && { conversationId }),
    },
  });

  return data.questions;
}

interface AutocompleteResponse {
  response: {
    results: Array<{
      id: string;
      question: string;
    }>;
  };
}

export async function fetchAutocomplete({
  endpoint,
  query,
  collectionId,
  headers,
  conversationId,
  allowedRegions,
}: {
  endpoint: string;
  query: string;
  collectionId?: string;
  headers?: Record<string, string>;
  conversationId?: string;
  allowedRegions?: string[];
}): Promise<AutocompleteResponse> {
  const data = await makeRequest<
    AutocompleteResponse,
    {
      query: string;
      collectionId?: string;
      conversationId?: string;
      allowedRegions?: string[];
    }
  >(endpoint, {
    method: 'POST',
    headers,
    retry: searchRetryConfig,
    body: {
      query,
      ...(collectionId && collectionId !== 'all' && { collectionId }),
      ...(conversationId && { conversationId }),
      ...(allowedRegions && { allowedRegions }),
    },
  });

  return data;
}

export async function fetchConversationHistory({
  endpoint,
  headers,
  externalUserId,
}: {
  endpoint: string;
  headers?: Record<string, string>;
  externalUserId?: string;
}): Promise<ConversationSummary[]> {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return Promise.reject(new TypeError(`Invalid conversation history endpoint: ${endpoint}`));
  }

  if (externalUserId) url.searchParams.set('externalUserId', externalUserId);

  const payload = await makeRequest<ConversationHistoryResponse>(url.toString(), {
    method: 'GET',
    headers,
  });

  return payload.data.map((item) => ({
    conversationId: item.conversationId,
    title: item.title,
    status: item.status,
    timestamp: formatDistance(new Date(item.lastActivityAt ?? item.createdAt)),
    isHandoff: item.isHandoff,
    agentAvatarUrl: item.agentAvatarUrl,
    subtitle: item.agentName ? `${item.agentName}` : undefined,
    hasUnread: item.hasUnread ?? false,
  }));
}

export async function markConversationRead({
  endpoint,
  conversationId,
  headers,
}: {
  endpoint: string;
  conversationId: string;
  headers?: Record<string, string>;
}): Promise<void> {
  await makeRequest(endpoint, {
    method: 'POST',
    headers,
    body: { conversationId },
  });
}

export async function markConversationCompleted({
  endpoint,
  conversationId,
  headers,
}: {
  endpoint: string;
  conversationId: string;
  headers?: Record<string, string>;
}): Promise<void> {
  await makeRequest(endpoint, {
    method: 'PATCH',
    headers,
    body: { conversationId },
  });
}
